import React, { useEffect, useRef, useState } from 'react';

/**
 * HeroVideo — poster-first, ultra-light background video layer.
 * - Poster image renders immediately (no flash / no layout shift) and is the graceful fallback.
 * - Video autoplays muted + looped + inline, fading in only once it can play.
 * - Skips video entirely for prefers-reduced-motion, data-saver / 2g, or autoplay failure.
 * - Never captures clicks (pointer-events: none) and stays clipped inside the hero.
 */
export default function HeroVideo({ poster, src720, src360, objectPosition = 'center', className = '' }) {
  const videoRef = useRef(null);
  const [src, setSrc] = useState(null);
  const [ready, setReady] = useState(false);

  // Decide whether (and which) video to load — client only, after first paint.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const conn = navigator.connection || navigator.webkitConnection || navigator.mozConnection;
    const saveData = !!(conn && (conn.saveData || /(^|\b)(slow-)?2g$/.test(conn.effectiveType || '')));
    if (reduce || saveData) return; // poster only
    const mobile = window.matchMedia('(max-width: 768px)').matches;
    setSrc((mobile ? src360 : src720) || src720 || src360);
  }, [src360, src720]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !src) return;
    v.muted = true;
    let done = false;
    const tryPlay = () => {
      const p = v.play();
      if (p && typeof p.then === 'function') {
        p.then(() => { done = true; setReady(true); }).catch(() => { if (!done) setReady(false); });
      } else {
        done = true;
        setReady(true);
      }
    };
    const onErr = () => setReady(false);
    v.addEventListener('canplay', tryPlay);
    v.addEventListener('loadeddata', tryPlay);
    v.addEventListener('error', onErr);
    // Kick off loading immediately (poster stays visible until the fade-in).
    try { v.load(); tryPlay(); } catch (e) { /* poster fallback */ }
    return () => {
      v.removeEventListener('canplay', tryPlay);
      v.removeEventListener('loadeddata', tryPlay);
      v.removeEventListener('error', onErr);
    };
  }, [src]);

  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`} aria-hidden="true" style={{ pointerEvents: 'none' }}>
      <img src={poster} alt="" draggable="false" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition }} />
      {src && (
        <video
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-[900ms] ease-out"
          style={{ objectPosition, opacity: ready ? 1 : 0 }}
          src={src}
          poster={poster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          disablePictureInPicture
          tabIndex={-1}
        />
      )}
    </div>
  );
}
